import { cn } from "@/lib/utils";

export const PageHeader = ({ title, subtitle, children, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-testid={testid}>
    <div>
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-950">{title}</h1>
      {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex gap-2 flex-wrap">{children}</div>}
  </div>
);

export const Card = ({ title, description, children, className = "", action, testid }) => (
  <section className={cn("admin-card p-5 sm:p-6", className)} data-testid={testid}>
    {(title || action) && (
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          {title && <h2 className="text-[15px] font-semibold text-zinc-950">{title}</h2>}
          {description && <p className="text-[13px] text-zinc-500 mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

const BADGE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  shipped: "bg-sky-50 text-sky-700 border-sky-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  unfulfilled: "bg-amber-50 text-amber-800 border-amber-200",
  draft: "bg-zinc-100 text-zinc-600 border-zinc-200",
  expired: "bg-zinc-100 text-zinc-500 border-zinc-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-violet-50 text-violet-700 border-violet-200",
  partially_refunded: "bg-violet-50 text-violet-700 border-violet-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  inactive: "bg-zinc-100 text-zinc-600 border-zinc-200",
  limit_reached: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const LABEL = {
  paid: "Paid",
  pending: "Awaiting payment",
  expired: "Abandoned",
  failed: "Payment failed",
  refunded: "Refunded",
  partially_refunded: "Partly refunded",
  unfulfilled: "To ship",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  published: "Published",
  draft: "Draft",
  active: "Active",
  scheduled: "Scheduled",
  inactive: "Inactive",
  limit_reached: "Limit reached",
};

export const StatusBadge = ({ status, testid }) => (
  <span
    data-testid={testid}
    className={`admin-status ${status} inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${BADGE[status] || BADGE.draft}`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
    {LABEL[status] || status}
  </span>
);

export const Btn = ({ variant = "primary", className = "", children, ...props }) => {
  const styles = {
    primary: "bg-zinc-950 text-white hover:bg-zinc-800 border-zinc-950",
    secondary: "bg-white text-zinc-900 hover:bg-zinc-50 border-zinc-300",
    danger: "bg-white text-red-600 hover:bg-red-50 border-red-200",
  };
  return (
    <button {...props} className={`inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border text-sm font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const FieldLabel = ({ children, hint }) => (
  <span className="block text-[13px] font-medium text-zinc-800 mb-1.5">
    {children} {hint && <span className="font-normal text-zinc-400">{hint}</span>}
  </span>
);

export const inputCls = "w-full h-10 px-3 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-950 placeholder:text-zinc-400 outline-none focus:border-zinc-950 focus:ring-2 focus:ring-zinc-950/10 transition-shadow";
export const textareaCls = "w-full px-3 py-2.5 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-950 placeholder:text-zinc-400 outline-none focus:border-zinc-950 focus:ring-2 focus:ring-zinc-950/10 transition-shadow";

export const EmptyState = ({ icon: Icon, title, text, children, testid }) => (
  <div className="text-center py-14 px-6" data-testid={testid}>
    {Icon && <div className="mx-auto w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-4"><Icon size={20} className="text-zinc-500" /></div>}
    <h3 className="font-semibold text-zinc-900">{title}</h3>
    {text && <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">{text}</p>}
    {children && <div className="mt-5">{children}</div>}
  </div>
);
